'use client'

import React from 'react'
import { X, TrendingUp, TrendingDown, MessageCircle, Calendar, Target } from 'lucide-react'
import { PerformanceScoreCard, PerformanceLevelBadge, CategoryBreakdownCard } from '.'
import { MetricCard } from '../MetricCard'
import {
  getPerformanceLevel,
  getPerformanceInsights
} from '../../lib/performance-utils'

interface TeamMember {
  id: string
  name: string
  role: string
  avatar?: string
  score: number
  trend: 'up' | 'down' | 'stable'
  level: string
  lastReview: string
  nextReview: string
}

interface TeamMemberDetailsModalProps {
  member: TeamMember | null
  isOpen: boolean
  onClose: () => void
}

const TeamMemberDetailsModal: React.FC<TeamMemberDetailsModalProps> = ({
  member,
  isOpen,
  onClose
}) => {
  if (!isOpen || !member) return null

  // TODO: Fix async data loading - using empty array for now
  const categories: any[] = []
  const insights = getPerformanceInsights(categories)
  const performanceLevel = getPerformanceLevel(member.score)

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-brand-dark-light border border-brand-gray/20 rounded-monumenta shadow-monumenta-brand w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-brand-gray/20">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand">
              <span className="text-lg font-bold text-brand-dark font-display">
                {member.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-display font-bold text-white">
                {member.name}
              </h2>
              <p className="text-brand-gray text-sm font-body">
                {member.role}
              </p>
            </div>
            <PerformanceLevelBadge level={performanceLevel.config.label} score={member.score} />
          </div>
          
          <button
            onClick={onClose}
            className="text-brand-gray hover:text-white transition-colors duration-200 p-2 hover:bg-brand-dark rounded-monumenta"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column - Performance Overview */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Performance Score Card */}
              <div className="flex justify-center lg:justify-start">
                <div className="w-64">
                  <PerformanceScoreCard
                    score={member.score}
                    level={performanceLevel.config.label}
                    levelColor={performanceLevel.config.color}
                    daysToNextReview={15}
                  />
                </div>
              </div>

              {/* Quick Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MetricCard
                  title="Última Avaliação"
                  value={member.lastReview}
                  icon={Calendar}
                />
                <MetricCard
                  title="Próxima Revisão"
                  value={member.nextReview}
                  icon={Target}
                />
                <MetricCard
                  title="Tendência"
                  value={member.trend === 'up' ? '+5%' : member.trend === 'down' ? '-3%' : '0%'}
                  icon={member.trend === 'up' ? TrendingUp : TrendingDown}
                  trend={member.trend === 'stable' ? 'neutral' : member.trend}
                />
                <MetricCard
                  title="Feedback Recente"
                  value="3"
                  change="Este mês"
                  icon={MessageCircle}
                />
              </div>

              {/* Category Breakdown */}
              <CategoryBreakdownCard categories={categories} />

              {/* Key Insights */}
              <div className="card">
                <h3 className="font-display text-lg font-semibold text-white mb-4">
                  Insights Principais
                </h3>
                <div className="space-y-3">
                  {/* Strengths */}
                  {insights.strengths.slice(0, 2).map((strength: string, index: number) => (
                    <div key={`strength-${index}`} className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-success" />
                      <p className="text-sm text-gray-300 font-body leading-relaxed">
                        Forte em {strength}
                      </p>
                    </div>
                  ))}
                  {/* Improvements */}
                  {insights.improvements.slice(0, 1).map((improvement: string, index: number) => (
                    <div key={`improvement-${index}`} className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-warning" />
                      <p className="text-sm text-gray-300 font-body leading-relaxed">
                        Área de melhoria: {improvement}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Actions & Recent Activity */}
            <div className="space-y-6">
              
              {/* Quick Actions */}
              <div className="card">
                <h3 className="font-display text-lg font-semibold text-white mb-4">
                  Ações Rápidas
                </h3>
                <div className="space-y-3">
                  <button className="btn-primary w-full justify-center">
                    <MessageCircle size={16} className="mr-2" />
                    Enviar Feedback
                  </button>
                  {/* HIDDEN: Schedule 1:1 button
                  <button className="btn-secondary w-full justify-center">
                    <Calendar size={16} className="mr-2" />
                    Agendar 1:1
                  </button>
                  */}
                  <button className="btn-ghost w-full justify-center">
                    <Target size={16} className="mr-2" />
                    Ver Metas
                  </button>
                </div>
              </div>

              {/* Performance History */}
              <div className="card">
                <h3 className="font-display text-lg font-semibold text-white mb-4">
                  Histórico Recente
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 pb-3 border-b border-brand-gray/10/50 last:border-0 last:pb-0">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">
                        Avaliação Q3 2024 concluída
                      </p>
                      <p className="text-xs text-brand-gray mt-1">
                        Score: {member.score.toFixed(1)} • {member.level}
                      </p>
                      <p className="text-xs text-brand-gray">Há 2 semanas</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 pb-3 border-b border-brand-gray/10/50 last:border-0 last:pb-0">
                    <div className="w-2 h-2 bg-success rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">
                        Feedback positivo recebido
                      </p>
                      <p className="text-xs text-brand-gray mt-1">
                        "Excelente liderança no projeto X"
                      </p>
                      <p className="text-xs text-brand-gray">Há 1 semana</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 pb-3 border-b border-brand-gray/10/50 last:border-0 last:pb-0">
                    <div className="w-2 h-2 bg-info rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">
                        Meta de desenvolvimento criada
                      </p>
                      <p className="text-xs text-brand-gray mt-1">
                        Melhorar habilidades de apresentação
                      </p>
                      <p className="text-xs text-brand-gray">Há 3 dias</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="card">
                <h3 className="font-display text-lg font-semibold text-white mb-4">
                  Próximos Passos
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 border-2 border-brand-primary rounded flex-shrink-0"></div>
                    <span className="text-sm text-gray-300 font-body">
                      Review mensal agendada para 15/12
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 border-2 border-warning rounded flex-shrink-0"></div>
                    <span className="text-sm text-gray-300 font-body">
                      Definir metas para Q1 2025
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 border-2 border-info rounded flex-shrink-0"></div>
                    <span className="text-sm text-gray-300 font-body">
                      Feedback 360° em janeiro
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamMemberDetailsModal
